import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Calculator } from "lucide-react";
import { API } from "../../lib/constants";
import { SEO } from "../../components/SEO";
import { Breadcrumbs, breadcrumbLd, GuideCard, GuideCta } from "../../components/guides/GuideParts";

const LOCAL_ZONES = ["Genève", "Annemasse", "Saint-Julien-en-Genevois", "Pays de Gex", "Haute-Savoie", "Ain"];

export default function GuidesHub() {
  const [data, setData] = useState(null);
  const [guides, setGuides] = useState([]);

  useEffect(() => {
    axios.get(`${API}/guides/categories`).then(({ data }) => setData(data)).catch(() => {});
    axios.get(`${API}/guides`).then(({ data }) => setGuides(data)).catch(() => {});
  }, []);

  const crumbs = [{ label: "Accueil", to: "/" }, { label: "Guides fiscaux frontaliers", to: "/guides-fiscaux-frontaliers" }];
  const pillar = guides.find((g) => g.slug === "declaration-impots-frontalier-suisse");
  const others = guides.filter((g) => g.slug !== "declaration-impots-frontalier-suisse");

  return (
    <main data-testid="guides-hub" className="pt-[76px]">
      <SEO
        title={data?.hub?.meta_title || "Guides fiscaux frontaliers Suisse-France | Fiscoconseils"}
        description={data?.hub?.meta_description || "Guides fiscaux pour frontaliers : déclaration impôt frontalier Suisse, Genève, TOU, DRIS, quasi-résident, 2047, 8TK."}
        path="/guides-fiscaux-frontaliers"
        jsonLd={[breadcrumbLd(crumbs), { "@context": "https://schema.org", "@type": "CollectionPage", name: "Guides fiscaux frontaliers", url: "https://www.fiscoconseils.com/guides-fiscaux-frontaliers", hasPart: guides.map((g) => ({ "@type": "Article", headline: g.title, url: `https://www.fiscoconseils.com/${g.slug}` })) }]}
      />
      <section className="bg-[#F8F9FA] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-10 pb-16">
          <Breadcrumbs items={crumbs} />
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#6B7280] mt-8 mb-4">Guides fiscaux frontaliers</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0A1128] leading-[1.05] max-w-4xl">
            Déclaration impôt frontalier Suisse : <span className="text-[#9CA3AF]">tous nos guides</span>
          </h1>
          <p className="text-base md:text-lg text-[#4B5563] mt-6 leading-relaxed max-w-3xl">{data?.hub?.intro}</p>
          <div className="mt-8 flex flex-wrap gap-2">
            {LOCAL_ZONES.map((z) => <span key={z} className="text-xs font-semibold text-[#0A1128] bg-white border border-[#C4C8CC]/60 rounded-full px-3 py-1">{z}</span>)}
          </div>
        </div>
      </section>

      {pillar && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-16">
          <Link to={`/${pillar.slug}`} data-testid="hub-pillar-link" className="group block bg-[#0A1128] rounded-2xl p-10 lg:p-14 relative overflow-hidden">
            <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-gold">Guide principal</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-white mt-3 max-w-3xl">{pillar.title}</h2>
            <p className="text-sm text-[#9CA3AF] mt-4 max-w-2xl leading-relaxed">{pillar.meta_description}</p>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-white mt-8">Lire le guide complet <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" /></span>
          </Link>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A1128]">Thématiques</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {(data?.categories || []).map((c) => (
            <Link key={c.slug} to={`/guides-fiscaux-frontaliers/${c.slug}`} data-testid={`hub-cat-${c.slug}`} className="group bg-white border border-[#C4C8CC]/50 rounded-2xl p-7 hover:border-[#0A1128]/40 transition-colors duration-200">
              <div className="flex items-center justify-between">
                <h3 className="text-base md:text-lg font-bold text-[#0A1128]">{c.name}</h3>
                <span className="text-xs font-semibold text-[#6B7280] bg-[#F8F9FA] border border-[#E5E7EB] rounded-full px-2.5 py-0.5">{c.count} guide{c.count > 1 ? "s" : ""}</span>
              </div>
              <p className="text-sm text-[#6B7280] mt-3 leading-relaxed">{c.description}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-[#F8F9FA] border-y border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A1128]">Tous les guides</h2>
            <Link to="/outils-frontaliers" data-testid="hub-tools-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A1128] border border-[#C4C8CC] rounded-full px-5 py-2.5 bg-white hover:bg-[#F1F2F4] transition-colors duration-200"><Calculator size={15} /> Outils gratuits frontaliers</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {others.map((g, i) => <GuideCard key={g.slug} g={g} index={i} />)}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#0A1128] max-w-2xl">Fiscaliste frontalier à Genève, Annemasse et dans le Pays de Gex</h2>
        <p className="text-base text-[#4B5563] mt-5 max-w-3xl leading-relaxed">
          Fiscoconseils accompagne les travailleurs frontaliers de Haute-Savoie et de l'Ain — Annemasse, Saint-Julien-en-Genevois, Gaillard, Ferney-Voltaire, Saint-Genis-Pouilly, Thonon, Annecy — ainsi que les résidents genevois et vaudois. Conseiller fiscal France-Suisse 100 % en ligne : déclaration frontalier, rectification de l'impôt à la source, TOU, quasi-résident, déclarations suisse et française.
        </p>
        <GuideCta title="Un fiscaliste frontalier pour votre dossier" text="Déclaration française avec revenus genevois, DRIS, TOU, quasi-résident : confiez votre dossier à un spécialiste de la fiscalité France-Suisse." />
      </section>
    </main>
  );
}
